import { useState } from 'react';

import { FileJson, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { BOOKING_STATUS_LABELS, BOOKING_STATUSES } from '@repo/shared-types';

import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { BookingNodeData, BookingStatus } from '@repo/shared-types';

interface BookingConfigProps {
  data: BookingNodeData;
  onChange: (data: BookingNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const BookingConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: BookingConfigProps) => {
  const update = (fields: Partial<BookingNodeData>) => {
    onChange({ ...data, ...fields });
  };

  const mappings = data.dataMappings ?? [];

  const updateMapping = (index: number, field: string, value: string) => {
    update({
      dataMappings: mappings.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    });
  };

  const addRow = () => update({ dataMappings: [...mappings, { field: '', value: '' }] });

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  // Nested object -> dotted field/value rows ({vehicle:{name:"x"}} -> vehicle.name = "x").
  const flattenToMappings = (
    obj: Record<string, unknown>,
    prefix = '',
  ): { field: string; value: string }[] => {
    const rows: { field: string; value: string }[] = [];
    for (const [key, val] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
        rows.push(...flattenToMappings(val as Record<string, unknown>, path));
      } else {
        rows.push({ field: path, value: val == null ? '' : String(val) });
      }
    }
    return rows;
  };

  const applyImport = () => {
    setImportError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(importText);
    } catch {
      setImportError('Invalid JSON');
      return;
    }

    const toRows = (arr: unknown[]): { field: string; value: string }[] | null => {
      const typed = arr as { field?: unknown; value?: unknown }[];
      if (!typed.every((r) => r && typeof r === 'object' && typeof r.field === 'string')) {
        return null;
      }
      return typed.map((r) => ({ field: r.field as string, value: String(r.value ?? '') }));
    };

    let rows: { field: string; value: string }[] | null;
    if (Array.isArray(parsed)) {
      rows = toRows(parsed);
      if (!rows) {
        setImportError('Array entries must be {"field": "...", "value": "..."} objects');
        return;
      }
    } else if (parsed && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>;
      // Accept a wrapper like the exported {"dataMappings": [...]} file directly
      if (Array.isArray(record.dataMappings)) {
        rows = toRows(record.dataMappings);
        if (!rows) {
          setImportError('dataMappings entries must be {"field": "...", "value": "..."} objects');
          return;
        }
      } else {
        rows = flattenToMappings(record);
      }
    } else {
      setImportError('Paste a JSON object or an array of {field, value} rows');
      return;
    }

    if (rows.length === 0) {
      setImportError('Nothing to import');
      return;
    }
    update({ dataMappings: rows });
    setImportOpen(false);
    setImportText('');
  };

  const removeRow = (index: number) =>
    update({ dataMappings: mappings.filter((_, i) => i !== index) });

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Label</label>
        <Input
          value={data.label || ''}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Booking"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Action</label>
        <Select
          value={data.action || 'add'}
          onValueChange={(v) => update({ action: v as BookingNodeData['action'] })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="add">Add Booking</SelectItem>
            <SelectItem value="cancel">Cancel Booking</SelectItem>
            <SelectItem value="set_status">Set Booking Status</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data.action === 'add' && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">
              Date (supports variables like {'{{call.availability_data.date}}'})
            </label>
            <VariableAutocomplete
              value={data.date || ''}
              onChange={(v) => update({ date: v })}
              placeholder="{{call.availability_data.date}}"
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">
              Time (supports variables like {'{{call.availability_data.time}}'})
            </label>
            <VariableAutocomplete
              value={data.time || ''}
              onChange={(v) => update({ time: v })}
              placeholder="{{call.availability_data.time}}"
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
            />
            <p className="mt-1 text-[10px] text-gray-500">
              e.g. 09:00, 14:30, or a variable. Leave empty for all-day.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">
              Title (supports variables)
            </label>
            <VariableAutocomplete
              value={data.title || ''}
              onChange={(v) => update({ title: v })}
              placeholder="{{contact.name}} - Shift"
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">
              Description (supports variables)
            </label>
            <VariableAutocomplete
              value={data.description || ''}
              onChange={(v) => update({ description: v })}
              placeholder="Optional description"
              multiline
              rows={3}
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">
              Output Key (optional)
            </label>
            <Input
              value={data.outputKey || ''}
              onChange={(e) => update({ outputKey: e.target.value || undefined })}
              placeholder="booking"
            />
            <p className="mt-1 text-[10px] text-gray-500">
              The created booking ({'{ id, date, time, title, status }'}) is available under this
              key downstream — use it to re-point a contact at the new booking after a reschedule,
              e.g. {'{{booking.id}}'}.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-400">Data mappings</label>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  onClick={() => {
                    setImportError(null);
                    setImportOpen((v) => !v);
                  }}
                >
                  <FileJson size={12} />
                  Import JSON
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  onClick={addRow}
                >
                  <Plus size={12} />
                  Add row
                </Button>
              </div>
            </div>

            {importOpen && (
              <div className="space-y-1 rounded-md border p-2">
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder='{"customer_name": "{{contact.name}}", "vehicle": {"registration": "{{call.lead_data.selected_vehicle.registration}}"}}'
                  rows={5}
                  className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2 py-1 font-mono text-xs focus-visible:ring-1 focus-visible:outline-none"
                />
                <p className="text-[10px] text-gray-500">
                  Paste a JSON object (nested keys become dotted fields) or an array of{' '}
                  {'{field, value}'} rows. Replaces the current mappings.
                </p>
                {importError && <p className="text-[11px] text-red-500">{importError}</p>}
                <div className="flex justify-end gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      setImportOpen(false);
                      setImportError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={applyImport}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}

            <div className="text-[10px] text-gray-500">
              Field is a dotted path inside the booking&apos;s data (e.g. vehicle.registration).
              Values support {'{{template}}'} variables, e.g.{' '}
              {'{{call.lead_data.selected_vehicle.registration}}'}.
            </div>

            {mappings.length === 0 && (
              <p className="text-xs text-gray-500 italic">
                No mappings yet. Click &quot;Add row&quot; to store structured data on the booking.
              </p>
            )}

            {mappings.map((mapping, index) => (
              <div key={index} className="flex items-center gap-1">
                <Input
                  value={mapping.field}
                  onChange={(e) => updateMapping(index, 'field', e.target.value)}
                  placeholder="field"
                  className="flex-1"
                />
                <span className="text-muted-foreground text-xs">&rarr;</span>
                <VariableAutocomplete
                  value={mapping.value}
                  onChange={(v) => updateMapping(index, 'value', v)}
                  placeholder="value"
                  responseFieldsSchema={responseFieldsSchema}
                  reviewNodeMetas={reviewNodeMetas}
                  classifierNodeMetas={classifierNodeMetas}
                  qualityNodeMetas={qualityNodeMetas}
                  agentNodeMetas={agentNodeMetas}
                  analyticsNodeMetas={analyticsNodeMetas}
                  className="flex-1"
                />
                <VariablePicker
                  onSelect={(v) => updateMapping(index, 'value', mapping.value + v)}
                  responseFieldsSchema={responseFieldsSchema}
                  reviewNodeMetas={reviewNodeMetas}
                  classifierNodeMetas={classifierNodeMetas}
                  qualityNodeMetas={qualityNodeMetas}
                  agentNodeMetas={agentNodeMetas}
                  analyticsNodeMetas={analyticsNodeMetas}
                  triggerType={triggerType}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive h-8 w-8 p-0"
                  onClick={() => removeRow(index)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {data.action !== 'add' && (
        <>
          {data.action === 'set_status' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-400">Status</label>
              <Select
                value={data.status || 'cancelled'}
                onValueChange={(v) => update({ status: v as BookingStatus })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BOOKING_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {BOOKING_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[10px] text-gray-500">
                The status written to the booking. Any booking not already in this status is
                updated, so a rescheduled booking can still be cancelled later.
              </p>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">
              Booking ID (optional)
            </label>
            <VariableAutocomplete
              value={data.bookingId || ''}
              onChange={(v) => update({ bookingId: v || undefined })}
              placeholder="{{contact.data.showroom_booking.booking_id}}"
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
            />
            <p className="mt-1 text-[10px] text-gray-500">
              Template resolving to a bookings row id — when it yields a UUID that exact booking is
              updated. Leave empty to fall back to the contact&apos;s phone (e.g.{' '}
              {'{{contact.data.showroom_booking.booking_id}}'}).
            </p>
          </div>
          <div className="rounded-md border border-yellow-500/20 bg-yellow-500/10 p-3 text-xs text-yellow-200">
            Without a booking ID, this applies to every future booking where the contact phone
            number matches the current call&apos;s contact.
          </div>
        </>
      )}
    </div>
  );
};
